import {
	AlertManager,
	useAlerts,
	useSelection,
	useSortHandler,
	useStableImperativeCallback,
	useValueEqualMemo
} from "client/Hooks";
import {
	delay,
	ensureNotNil,
	isNotNilOrEmpty
} from "client/Misc/utils";
import { hasImplicitDependencyOn } from "client/Misc/reactUtils";
import {
	DocumentsViewModel,
	GridFolderContentsModel,
	DocumentItemViewModel,
	FileViewModel,
	FolderViewModel,
	OneNoteNotebookViewModel,
	ListSortDirection,
	PagePosition,
	Permissions,
	QueryParametersModel,
	SearchScope,
	DocumentItemType,
	FileTypeFilterModel
} from "client/Api/Documents/SharePoint/common";
import { Folderish } from "client_legacy/Documents/SharePoint/models";
import {
	Dispatch,
	ReactElement,
	RefObject,
	SetStateAction,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState
} from "react";
import {
	isCancelledError
} from "client_legacy/ndaw/cancellation";
import { localize } from "client_legacy/ndaw/culture";
import { documentsConfiguration } from "client_legacy/ndaw/workspaceAbilitiesStatusProvider";
import {
	createDefaultPagePositionMap,
	generateSharePointUniqueKey,
	GridItem,
	gridItemPropertyNameToColumnIdMap,
	retrieveSharepointFolderContent,
	SharePointLocationParameters,
	toGridItem,
	useSharePointLocationParameters
} from "../../SharePoint/SharePointOverView";
import { SharePointFilter } from "../../SharePoint/SharePointSearchForm";
import styles from "./SharePointPickerOverview.module.less";
import { DataSourceSortableState, DataSourceSortDirection } from "client/Data/sorting";
import { CommonItemPickerOptions, ItemPickerMode } from "../ItemPicker";
import { SharePointEvent, subscribe } from "client_legacy/Documents/SharePoint/documentEvents";
import { unsubscribe } from "pubsub-js";
import { SharePointPickerListView, SharePointPickerListViewHandle } from "./SharePointPickerListView";
import { UserEditorPreference } from "client_legacy/Documents/officeDocumentsOpenPreference";
import { useEditorPreference } from "../../useEditorPreference";
import {
	OpenActionHandlers,
	getOpenActions,
	SharePointDocumentAction,
	SharePointDocumentActionContext
} from "../../SharePoint/actions";
import { reduceToAvailableActions } from "client_legacy/ndaw/actions";
import { SharePointPickerTableView, SharePointPickerTableViewHandle } from "./SharePointPickerTableView";
import { SyncClient } from "client/Api/SharePointSettings/sharepointSettings";
import { never } from "client_legacy/ndaw/errors";

export type SharePointPickerOverviewProps = {
	view: DocumentsViewModel;
	folder: Folderish;
	onOpenItem: (folder: Folderish) => void;
	commonOptions: CommonItemPickerOptions;
	filter: SharePointFilter;
	onFilesSelected: (files: readonly GridItem[]) => void;
	selectedItems: readonly GridItem[];
	onFolderPermissionsLoaded: (
		viewId: string,
		folderId: number | null,
		permissions: Permissions
	) => void;
	isMobile: boolean;
	sortingState: DataSourceSortableState<GridItem>;
	setSortingState: Dispatch<SetStateAction<DataSourceSortableState<GridItem>>>;
	shouldOpenFileOnClick: boolean;
};

export const SharePointPickerOverview = (props: SharePointPickerOverviewProps): ReactElement => {
	const {
		folder,
		onOpenItem,
		view,
		commonOptions,
		filter,
		onFolderPermissionsLoaded,
		onFilesSelected,
		selectedItems,
		isMobile,
		sortingState,
		setSortingState,
		shouldOpenFileOnClick
	} = props;

	const [isBusy, setIsBusy] = useState<boolean>(false);
	const [items, setItems] = useState<readonly GridItem[]>([]);
	const [currentPageNumber, setCurrentPageNumber] = useState<number>(0);
	const [pagePositions, setPagePositions] = useState<Map<number, PagePosition | null>>(
		() => createDefaultPagePositionMap()
	);

	const alertManager: AlertManager = useAlerts();
	const locationParameters: SharePointLocationParameters = useSharePointLocationParameters(view, folder);

	const {
		actionContext,
		getAvailableOpenActions,
		openItemHandler
	} = useSharePointPickerActionsAndHandlers(
		view,
		folder,
		onFilesSelected,
		onOpenItem,
		items,
		setItems,
		filter,
		shouldOpenFileOnClick,
		isMobile,
		alertManager
	);

	const { sortingFor } = useSortHandler<GridItem>(
		sortingState,
		setSortingState,
		true
	);

	const scrollContainerRef: RefObject<SharePointPickerTableViewHandle | SharePointPickerListViewHandle | null>
		= useRef<SharePointPickerTableViewHandle | SharePointPickerListViewHandle>(null);

	const isMultiSelect = useMemo(
		(): boolean => commonOptions.mode === ItemPickerMode.MultipleFiles
			|| commonOptions.mode === ItemPickerMode.MultipleFilesOrOneNoteNotebooks,
		[commonOptions.mode]
	);

	const queryParameters: QueryParametersModel = useQueryParameters(
		currentPageNumber,
		pagePositions,
		filter,
		sortingState,
		commonOptions.fileTypeFilter
	);

	useEffect(
		(): void => {
			hasImplicitDependencyOn(folder, currentPageNumber);
			onFilesSelected([]);
		},
		[
			folder,
			currentPageNumber,
			onFilesSelected
		]
	);

	useEffect(
		(): void => {
			hasImplicitDependencyOn(
				locationParameters.view.id,
				locationParameters.folder.id,
				queryParameters.sortingField,
				queryParameters.sortingDirection,
				queryParameters.filterText,
				queryParameters.searchScope
			);

			setCurrentPageNumber(0);
			setPagePositions(createDefaultPagePositionMap());
			setItems([]);

			const scrollContainer: SharePointPickerTableViewHandle | SharePointPickerListViewHandle | null
				= scrollContainerRef.current;

			scrollContainer?.resetScrollPosition();
		},
		[
			locationParameters.view.id,
			locationParameters.folder.id,
			queryParameters.sortingField,
			queryParameters.sortingDirection,
			queryParameters.filterText,
			queryParameters.searchScope
		]
	);

	// We don't want to trigger the loading of the page when the onFolderPermisionsLoaded callback changes.
	// we only care about sharing the information to the "last" version of the callback when we did
	// load permisions
	const stableOnFolderPermissionsLoaded: typeof onFolderPermissionsLoaded = useStableImperativeCallback(onFolderPermissionsLoaded);

	useEffect(
		() => {
			const loadPage = async (
				queryParameters: QueryParametersModel,
				signal: AbortSignal
			): Promise<void> => {

				setIsBusy(true);

				const {
					view,
					folder
				} = locationParameters;

				try {
					// Wait for all the dependencies which trigger a load to settle to prevent
					// requests getting aborted
					await delay(10, signal);

					const folderContent: GridFolderContentsModel = await retrieveSharepointFolderContent(
						folder,
						queryParameters,
						view.type,
						signal
					);

					const {
						items,
						pagerParameters,
						permissions
					} = folderContent;

					setPagePositions(
						x => {
							const result = new Map<number, PagePosition | null>(x);

							result.set(
								currentPageNumber,
								pagerParameters.nextPagePosition
							);

							return result;
						}
					);

					const newGridItems: GridItem[] = items
						.map(
							(x) => toGridItem(x, folder)
						);

					setItems(
						(x) => {
							const newUniqueKeys = new Set<string>(
								x.map(x => x.uniqueKey)
							);

							const result: GridItem[] = [...x];
							for (const item of newGridItems) {
								if (newUniqueKeys.has(item.uniqueKey)) {
									continue;
								}

								result.push(item);
							}

							return result;
						}
					);

					if (permissions) {
						stableOnFolderPermissionsLoaded?.(
							view.id,
							folder.id,
							permissions
						);
					}

					setIsBusy(false);
				} catch (e) {
					if (isCancelledError(e)) {
						return;
					}

					setIsBusy(false);
					alertManager.showError(localize("_GeneralErrorMessage"));
				}
			};

			const cancellation = new AbortController();
			loadPage(queryParameters, cancellation.signal);

			return () => cancellation.abort();
		},
		[
			alertManager,
			currentPageNumber,
			locationParameters,
			stableOnFolderPermissionsLoaded,
			queryParameters
		]
	);

	const selectableItems: GridItem[] = useMemo(
		() => {
			return items.filter(
				(x: GridItem): boolean => {
					if (x.model.itemType === DocumentItemType.Folder) {
						return false;
					}

					return !checkIsDocumentDisabled(x, commonOptions.mode);
				}
			);
		},
		[
			commonOptions.mode,
			items
		]
	);

	const {
		multiSelection,
		selectionFor
	} = useSelection(
		selectableItems,
		"uniqueKey",
		{
			isMultiSelect: true,
			selectedItems,
			setSelectedItems: onFilesSelected
		}
	);

	const loadMoreItemsHandler = useCallback(
		(): void => {
			setCurrentPageNumber(x => x + 1);
		},
		[]
	);

	const renderContent = (): ReactElement => {
		const hasMoreItems: boolean = pagePositions.has(currentPageNumber)
			&& pagePositions.get(currentPageNumber) !== null;

		if (isMobile) {
			return (
				<SharePointPickerListView
					ref={scrollContainerRef}
					isBusy={isBusy}
					hasMoreItems={hasMoreItems}
					onLoadMoreItems={loadMoreItemsHandler}
					loadMoreIndicatorVersion={currentPageNumber}
					items={items}
					selectionFor={selectionFor}
					onOpenItem={openItemHandler}
					mode={commonOptions.mode}
					isInSelectMode={isMultiSelect}
					getAvailableOpenActions={getAvailableOpenActions}
					actionContext={actionContext}
					hasSearch={isNotNilOrEmpty(filter.text.value)}
				/>
			);
		}

		return (
			<SharePointPickerTableView
				isBusy={isBusy}
				hasMoreItems={hasMoreItems}
				onLoadMoreItems={loadMoreItemsHandler}
				loadMoreIndicatorVersion={currentPageNumber}
				sortingFor={sortingFor}
				multiSelection={isMultiSelect ? multiSelection : undefined}
				selectionFor={selectionFor}
				items={items}
				onOpenFolder={openItemHandler}
				ref={scrollContainerRef}
				mode={commonOptions.mode}
				shouldShowLocationColumn={actionContext.isInSearchResults}
				hasSearch={isNotNilOrEmpty(filter.text.value)}
			/>
		);
	};

	return (
		<div className={styles.root}>
			<div className={styles.itemsContainer}>
				{renderContent()}
			</div>
		</div>
	);
};
SharePointPickerOverview.displayName = "SharePointPickerOverview";

export type SharePointFile = FileViewModel | OneNoteNotebookViewModel;

function useQueryParameters(
	currentPageNumber: number,
	pagePositions: Map<number, PagePosition | null>,
	filter: SharePointFilter,
	sortingState: DataSourceSortableState<GridItem>,
	fileTypeFilter?: FileTypeFilterModel
): QueryParametersModel {

	const currentPagePosition = useMemo(
		(): PagePosition | null => {
			return currentPageNumber === 0
				? null
				: ensureNotNil(
					pagePositions.get(currentPageNumber - 1)
				);
		},
		[currentPageNumber, pagePositions]
	);

	return useValueEqualMemo(
		(): QueryParametersModel => {
			if (sortingState.direction === DataSourceSortDirection.None) {
				throw new Error("An incorrect sorting is specified");
			}

			const {
				scope,
				text: {
					value: searchTerm
				}
			} = filter;

			return {
				itemsCount: documentsConfiguration().sharePoint
					.pageSize,
				sortingField: ensureNotNil(gridItemPropertyNameToColumnIdMap[sortingState.propertyName]),
				sortingDirection: sortingState.direction === DataSourceSortDirection.Ascending
					? ListSortDirection.Ascending
					: ListSortDirection.Descending,
				pagePosition: currentPagePosition,
				fileTypeFilter: fileTypeFilter ?? null,
				filterText: searchTerm,
				searchScope: scope
			};
		},
		[
			sortingState,
			filter,
			currentPagePosition,
			fileTypeFilter
		]
	);
}

export function checkIsDocumentDisabled(
	document: GridItem,
	mode: ItemPickerMode
): boolean {
	if (document.model.itemType === DocumentItemType.Folder) {
		return false;
	}

	switch (mode) {
		case ItemPickerMode.SingleFolder:
			return true;
		case ItemPickerMode.MultipleFiles:
			return document
				.model
				.itemType === DocumentItemType.OneNoteNotebook;
		case ItemPickerMode.MultipleFilesOrOneNoteNotebooks:
		case ItemPickerMode.SingleFileOrOneNoteNotebook:
			return false;
		default:
			never(mode);
	}
}

type SharePointPickerActionsAndHandlers = {
	actionContext: SharePointDocumentActionContext;
	getAvailableOpenActions: (item: DocumentItemViewModel) => readonly SharePointDocumentAction[];
	openItemHandler: (item: GridItem) => void;
};

export function useSharePointPickerActionsAndHandlers(
	view: DocumentsViewModel,
	folder: Folderish,
	onFilesSelected: (files: readonly GridItem[]) => void,
	onOpenItem: (folder: Folderish) => void,
	items: readonly GridItem[],
	setItems: Dispatch<SetStateAction<readonly GridItem[]>>,
	filter: SharePointFilter,
	shouldOpenFileOnClick: boolean,
	isMobile: boolean,
	alertManager: AlertManager
): SharePointPickerActionsAndHandlers {
	const editorPreference: UserEditorPreference = useEditorPreference();

	const isInSearchResults: boolean = filter.scope === SearchScope.Subfolders
		&& (filter.text.value !== "");

	const actionContext = useMemo(
		(): SharePointDocumentActionContext => {
			return {
				view,
				folder,
				selection: [],
				isInSearchResults,
				isSharingViaLinkEnabled: false,
				isVersioningEnabled: false,
				maxNumberOfFilesToDownloadAsZip: 1,
				syncClient: SyncClient.None,
				isMobile,
				isInSelectMode: false
			};
		},
		[
			view,
			folder,
			isInSearchResults,
			isMobile
		]
	);

	const actionHandlers = useMemo(
		(): OpenActionHandlers => {
			return {
				openFolder: (folder: FolderViewModel): void => {
					onOpenItem({ ...folder, isRoot: false });
				},
				selectItem: (item: DocumentItemViewModel): void => {
					const itemUniqueKey: string = generateSharePointUniqueKey(item);
					onFilesSelected(
						[
							ensureNotNil(
								items.find(x => x.uniqueKey === itemUniqueKey)
							)
						]
					);
				}
			};
		},
		[
			items,
			onOpenItem,
			onFilesSelected
		]
	);

	const getAvailableOpenActions = useCallback(
		(item: DocumentItemViewModel): readonly SharePointDocumentAction[] => {
			const context: SharePointDocumentActionContext = {
				...actionContext,
				selection: [item]
			};

			return reduceToAvailableActions(
				getOpenActions(
					actionHandlers,
					items.map(x => x.model),
					alertManager,
					editorPreference
				),
				context
			);
		},
		[
			actionContext,
			actionHandlers,
			alertManager,
			items,
			editorPreference
		]
	);

	const openItemHandler = useCallback(
		(item: GridItem): void => {
			if (!shouldOpenFileOnClick && item.model.itemType !== DocumentItemType.Folder) {
				return;
			}

			getAvailableOpenActions(item.model)[0]
				?.execute({...actionContext, selection: [item.model]});
		},
		[
			actionContext,
			getAvailableOpenActions,
			shouldOpenFileOnClick
		]
	);

	const documentCreatedHandler = useCallback(
		(newDocument: DocumentItemViewModel): void => {
			setItems(items => ([
				...items,
				toGridItem(newDocument, folder)
			]));

			if (newDocument.itemType === DocumentItemType.Folder) {
				onOpenItem({...newDocument, isRoot: false});
			}
		},
		[
			folder,
			onOpenItem,
			setItems
		]
	);

	useEffect(
		() => {
			const subscriptionTokens = subscribe(
				SharePointEvent.AddedOrReplaced,
				(_, data) => documentCreatedHandler(data)
			);

			return () => {
				unsubscribe(subscriptionTokens);
			};
		},
		[documentCreatedHandler]
	);

	return {
		actionContext,
		getAvailableOpenActions,
		openItemHandler
	};
}